import { Component, inject, Input, signal, WritableSignal } from '@angular/core';
import { BasicForm } from '@shared/components/classes/basic.form';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UsersService } from '@modules/settings/users/users.service';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { RoleIdLabels, Roles } from '@common/rbac/roles';
import { removeEmptyProperties } from '@shared/utils/object.utils';
import { AuthService } from '@app/auth/auth.service';
import { Observable } from 'rxjs';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { ApiService } from '@shared/services/api.service';
import { FormSkeletonComponent } from '@common/components/form-skeleton/form-skeleton.component';
import { ButtonModule } from '@primeng/button';
import { finalize } from 'rxjs/operators';
import { NgIf } from '@angular/common';
import urlJoin from 'url-join';
import { environment } from '@environments/environment';
import { ModalClose } from '@shared/services/modal/modal.enum';
import { NgCanRoleDirective } from '@common/rbac/ng-can-role.directive';
import { PasswordModule } from '@primeng/password';
import { CompanyType } from '@modules/universal-billing/companies/companies.interfaces';
import { CompaniesService } from '@modules/universal-billing/companies/companies.service';
import { RoleService } from '@common/rbac/role.service';

@Component({
  selector: 'user-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputValidatorDirective,
    TranslatePipe,
    AutocompleteComponent,
    NgLabelTemplateDirective,
    FormSkeletonComponent,
    ButtonModule,
    NgIf,
    NgCanRoleDirective,
    PasswordModule,
    NgOptionTemplateDirective,
    NgSelectComponent,

  ],
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
})
export class UserFormComponent extends BasicForm<any> {
  _defaultService: UsersService = inject(UsersService);
  form: FormGroup;
  authService: AuthService = inject(AuthService);
  api = inject(ApiService);
  @Input() profile: boolean = false;
  vision: boolean = true;
  photoThumb: WritableSignal<string> = signal(null);
  uploadingPhoto = signal(false);
  protected readonly Roles = Roles;
  companyService = inject(CompaniesService);
  roles = [];

  constructor(private fb: FormBuilder) {
    super();
    this.form = this.fb.group({
      email: [null, [Validators.required, Validators.email]],
      phoneNumber: [null, Validators.required],
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      userName: [null, Validators.required],
      status: [true, Validators.required],
      password: ['']
    });
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.profile) {
      const user = this.authService.userData()
      this.form.patchValue({
        ...user
      })
      if (user.photoThumb) {
        this.photoThumb.set(urlJoin(environment.storageUrl, user.photoThumb));
      }
    } else {
      this.form.addControl('roleId', new FormControl(Roles.TENANT, [Validators.required]));
      this.form.addControl('companyId', new FormControl(null, [Validators.required]));
      this.form.get('password').setValidators(this.isEdit ? [] : [Validators.required]);
      this.roles = this.filterRoles();
    }
  }
  filterRoles() {
    switch (RoleService.currentRole) {
      case Roles.ADMIN:
        return [Roles.ADMIN, Roles.COMPANY_GROUP_MANAGER, Roles.COMPANY_MANAGER, Roles.BUILDING_MANAGER, Roles.TENANT];
      case Roles.COMPANY_GROUP_MANAGER:
        return [Roles.COMPANY_MANAGER, Roles.BUILDING_MANAGER, Roles.TENANT];
      case Roles.COMPANY_MANAGER:
        return [Roles.BUILDING_MANAGER, Roles.TENANT];
      case Roles.BUILDING_MANAGER:
        return [Roles.TENANT];
      default:
        return [];
    }
  }
  submitAction(data: object): Observable<any> {
    return this.profile ? this._defaultService.updateProfile(data) : super.submitAction(data);
  }

  convertToRequestData(data: any): any {
    return super.convertToRequestData(removeEmptyProperties(data));
  }

  afterSuccessSubmit(res: any) {
    if (this.profile) {
      this.authService.userData.set({
        ...this.authService.userData(),
        ...this.form.value,
      });
    }
    this.isPending.set(false);
    this.sweetAlertService.success('', this.isEdit || this.profile ? "TOAST.TOAST_UPDATE" : "TOAST.TOAST_CREATE")
    this._modalService.close(ModalClose.SUCCESS);
  }


  patchResponseData(res: any) {
    res.roleId = res.roles?.[0]?.id ?? Roles.TENANT;
    super.patchResponseData(res);
  }


  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const formData = new FormData();
    formData.append('photo', file);

    this.uploadingPhoto.set(true);

    this.api
      .post('/users/edit/photo', formData)
      .pipe(finalize(() => this.uploadingPhoto.set(false)))
      .subscribe({
        next: (res: any) => {
          this.photoThumb.set(urlJoin(environment.storageUrl, res.data.photoThumb));
          this.authService.userData.update(u => ({
            ...u,
            photo: res.data.photo,
            photoThumb: res.data.photoThumb
          }));
          this._toastService.success('', "TOAST.TOAST_PHOTO_UPDATE");
        },
        error: err => this._toastService.error(err.error.message)
      });
  }

  deletePhoto() {
    this.api.delete('/users/edit/photo').subscribe({
      next: () => {
        this.photoThumb.set(null);
        this.authService.userData.update(u => ({
          ...u,
          photo: null,
          photoThumb: null
        }));
        this._toastService.success( '', "TOAST.TOAST_PHOTO_DELETE");
      },
      error: err => this._toastService.error(err.message)
    });
  }
  getCompanies(params: any) {
    params.companyType = params.roleId == Roles.COMPANY_GROUP_MANAGER ? CompanyType.COMPANY_GROUP : CompanyType.COMPANY;
    return this.companyService.getActiveAll(params);
  }

  protected readonly RoleService = RoleService;
  protected readonly RoleIdLabels = RoleIdLabels;
}
